import { createAsyncThunk, createEntityAdapter, createSlice, } from '@reduxjs/toolkit';
import { ActorType, } from '../types/conversation.types';
import { updateConversationTitle as updateConversationTitleThunk } from './thunks/conversationThunks';
const assistantConversationsAdapter = createEntityAdapter({
    sortComparer: (a, b) => {
        var _a, _b, _c, _d;
        const aTime = (_b = (_a = a.updated_at) !== null && _a !== void 0 ? _a : a.created_at) !== null && _b !== void 0 ? _b : '';
        const bTime = (_d = (_c = b.updated_at) !== null && _c !== void 0 ? _c : b.created_at) !== null && _d !== void 0 ? _d : '';
        return bTime.localeCompare(aTime);
    },
});
export const fetchConversations = createAsyncThunk('assistantConversations/fetchConversations', async ({ api, limit = 50, offset = 0 }) => {
    const response = await api.listConversations(limit, offset);
    return response;
});
export const deleteConversation = createAsyncThunk('assistantConversations/deleteConversation', async ({ api, conversationId }) => {
    await api.deleteConversation(conversationId);
    return conversationId;
});
const initialState = {
    conversations: assistantConversationsAdapter.getInitialState(),
    activeId: null,
    status: 'idle',
    error: null,
};
const assistantConversationSlice = createSlice({
    name: 'assistantConversations',
    initialState,
    reducers: {
        addConversation: (state, action) => {
            assistantConversationsAdapter.upsertOne(state.conversations, action.payload);
        },
        updateConversation: (state, action) => {
            assistantConversationsAdapter.updateOne(state.conversations, {
                id: action.payload.id,
                changes: action.payload,
            });
        },
        removeConversation: (state, action) => {
            assistantConversationsAdapter.removeOne(state.conversations, action.payload);
            if (state.activeId === action.payload) {
                state.activeId = null;
            }
        },
        setActiveConversation: (state, action) => {
            state.activeId = action.payload;
        },
        clearConversations: (state) => {
            assistantConversationsAdapter.removeAll(state.conversations);
            state.activeId = null;
        },
        clearActiveConversation: (state) => {
            state.activeId = null;
        },
        invalidateConversations: (state) => {
            state.status = 'idle';
            state.error = null;
        },
        updateConversationActivity: (state, action) => {
            const conversationId = action.payload;
            const currentTime = new Date().toISOString();
            assistantConversationsAdapter.updateOne(state.conversations, {
                id: conversationId,
                changes: { updated_at: currentTime },
            });
        },
        updateConversationTitle: (state, action) => {
            const { conversationId, title } = action.payload;
            assistantConversationsAdapter.updateOne(state.conversations, {
                id: conversationId,
                changes: { title },
            });
        },
        setStatus: (state, action) => {
            state.status = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchConversations.pending, (state) => {
            state.status = 'pending';
            state.error = null;
        })
            .addCase(fetchConversations.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.error = null;
            const items = Array.isArray(action.payload) ? action.payload : [];
            const activeConversation = state.activeId
                ? state.conversations.entities[state.activeId]
                : undefined;
            const conversations = items.map((item) => {
                var _a, _b, _c, _d, _e;
                return ({
                    id: item.id,
                    title: item.title,
                    updated_at: item.updated_at,
                    created_at: item.updated_at || new Date().toISOString(),
                    created_by_id: '',
                    created_by_type: ActorType.ADMIN,
                    root_conversation_id: null,
                    usage_context: (_a = item.usage_context) !== null && _a !== void 0 ? _a : null,
                    program_id: (_b = item.program_id) !== null && _b !== void 0 ? _b : null,
                    company_name: (_c = item.company_name) !== null && _c !== void 0 ? _c : null,
                    application_display: (_d = item.application_display) !== null && _d !== void 0 ? _d : null,
                    agent_mode: (_e = item.agent_mode) !== null && _e !== void 0 ? _e : null,
                });
            });
            assistantConversationsAdapter.setAll(state.conversations, conversations);
            if (activeConversation &&
                !state.conversations.entities[activeConversation.id]) {
                assistantConversationsAdapter.addOne(state.conversations, activeConversation);
            }
        })
            .addCase(fetchConversations.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.error.message || 'Failed to fetch conversations';
        })
            .addCase(deleteConversation.pending, (state, action) => {
            const conversationId = action.meta.arg.conversationId;
            assistantConversationsAdapter.updateOne(state.conversations, {
                id: conversationId,
                changes: { isDeleting: true },
            });
        })
            .addCase(deleteConversation.fulfilled, (state, action) => {
            const conversationId = action.payload;
            const wasActive = state.activeId === conversationId;
            assistantConversationsAdapter.removeOne(state.conversations, conversationId);
            if (wasActive) {
                const remainingConversations = assistantConversationsAdapter
                    .getSelectors()
                    .selectAll(state.conversations);
                const nextConversation = remainingConversations[0];
                if (nextConversation) {
                    state.activeId = nextConversation.id;
                }
                else {
                    state.activeId = null;
                }
            }
        })
            .addCase(deleteConversation.rejected, (state, action) => {
            const conversationId = action.meta.arg.conversationId;
            assistantConversationsAdapter.updateOne(state.conversations, {
                id: conversationId,
                changes: { isDeleting: false },
            });
            state.error = action.error.message || 'Failed to delete conversation';
        })
            .addCase(updateConversationTitleThunk.fulfilled, (state, action) => {
            const { conversationId, title } = action.payload;
            assistantConversationsAdapter.updateOne(state.conversations, {
                id: conversationId,
                changes: { title },
            });
        })
            .addCase(updateConversationTitleThunk.rejected, (state, action) => {
            state.error =
                action.error.message || 'Failed to update conversation title';
        });
    },
});
export const selectActiveConversationId = (state) => state.conversations.activeId;
export const { addConversation, updateConversation, removeConversation, setActiveConversation, clearConversations, clearActiveConversation, invalidateConversations, updateConversationActivity, updateConversationTitle, setStatus, setError, } = assistantConversationSlice.actions;
export const assistantConversationReducer = assistantConversationSlice.reducer;
export const conversationsSelectors = assistantConversationsAdapter.getSelectors((state) => state.conversations.conversations);
export { assistantConversationsAdapter, assistantConversationSlice };
